import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = []
  static values = {
    patientId: Number,
    appointmentId: Number
  }

  connect() {
    this.searchTimeout = null
  }

  async toggleDislike(event) {
    const checkbox = event.target
    const componentId = checkbox.dataset.componentId
    const componentName = checkbox.dataset.componentName
    const isDisliked = checkbox.checked

    // Mostrar mensaje de estado
    this.showStatusMessage(`Procesando ${componentName}...`, 'info')

    try {
      if (isDisliked) {
        // Crear dislike
        await this.createDislike(componentId, componentName)
      } else {
        // Eliminar dislike
        await this.removeDislike(componentId, componentName)
      }
    } catch (error) {
      console.error('Error toggling dislike:', error)
      // Revertir el estado del checkbox
      checkbox.checked = !isDisliked
      this.showStatusMessage(`Error al procesar ${componentName}`, 'danger')
    }
  }

  async createDislike(componentId, componentName) {
    const response = await fetch(`/patients/${this.patientIdValue}/food_dislikes`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content,
        'Accept': 'application/json'
      },
      body: JSON.stringify({
        food_dislike: {
          dislikeable_type: 'Component',
          dislikeable_id: componentId
        },
        redirect_to: 'food_dislikes'
      })
    })

    if (response.ok) {
      this.showStatusMessage(`${componentName} agregado a la lista de excluidos`, 'success')
      this.addBadgeToSummary(componentName)
    } else {
      throw new Error('Failed to create dislike')
    }
  }

  async removeDislike(componentId, componentName) {
    // Primero necesitamos encontrar el food_dislike_id
    const response = await fetch(`/patients/${this.patientIdValue}/food_dislikes`, {
      method: 'GET',
      headers: {
        'Accept': 'application/json'
      }
    })

    if (!response.ok) {
      throw new Error('Failed to fetch food dislikes')
    }

    const data = await response.json()
    const foodDislike = data.find(fd =>
      fd.dislikeable_type === 'Component' &&
      fd.dislikeable_id == componentId
    )

    if (!foodDislike) {
      throw new Error('Food dislike not found')
    }

    const deleteResponse = await fetch(`/food_dislikes/${foodDislike.id}`, {
      method: 'DELETE',
      headers: {
        'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content,
        'Accept': 'application/json'
      }
    })

    if (deleteResponse.ok) {
      this.showStatusMessage(`${componentName} eliminado de la lista de excluidos`, 'success')
      this.removeBadgeFromSummary(componentName)
    } else {
      throw new Error('Failed to delete dislike')
    }
  }

  showStatusMessage(message, type) {
    const messageElement = document.getElementById('toggle-status-message')
    const messageText = document.getElementById('toggle-message-text')

    messageText.textContent = message
    messageElement.className = `alert alert-${type}`
    messageElement.classList.remove('d-none')

    // Ocultar el mensaje después de 5 segundos (más tiempo para mensajes de búsqueda)
    const timeout = type === 'info' ? 5000 : 4000
    setTimeout(() => {
      messageElement.classList.add('d-none')
    }, timeout)
  }

  addBadgeToSummary(componentName) {
    const summaryContainer = document.querySelector('.food-preferences-summary')
    if (summaryContainer) {
      const badge = document.createElement('span')
      badge.className = 'badge px-3 mr-2 mb-2 rounded-pill'
      badge.style.backgroundColor = '#8B0000'
      badge.style.color = 'white'
      badge.textContent = componentName
      summaryContainer.appendChild(badge)
    }
  }

  removeBadgeFromSummary(componentName) {
    const summaryContainer = document.querySelector('.food-preferences-summary')
    if (summaryContainer) {
      const badges = summaryContainer.querySelectorAll('.badge')
      badges.forEach(badge => {
        if (badge.textContent === componentName) {
          badge.remove()
        }
      })
    }
  }

  searchFoods(event) {
    const searchTerm = event.target.value.toLowerCase().trim()

    // Limpiar timeout anterior
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout)
    }

    if (searchTerm === '') {
      // Si no hay término de búsqueda, quitar resaltado y ocultar spinner
      this.clearSearchResults()
      this.hideSpinner()
      return
    }

    // Mostrar spinner mientras espera el debounce
    this.showSpinner()

    // Debounce: esperar 500ms después de que el usuario deje de escribir
    this.searchTimeout = setTimeout(() => {
      this.performSearch(searchTerm)
    }, 500)
  }

  async performSearch(searchTerm) {
    const foodItems = document.querySelectorAll('.food-item-toggle')

    // Mostrar mensaje de búsqueda
    this.showStatusMessage('Buscando ingredientes...', 'info')

    // Dividir el término de búsqueda en palabras individuales
    const searchWords = searchTerm.split(/\s+/).filter(word => word.length > 0)
    let foundItems = []
    let itemsToSave = []
    let alreadyMarkedItems = []
    let foundWords = new Set()
    let notFoundWords = []

    // Analizar cada palabra de búsqueda individualmente
    searchWords.forEach(searchWord => {
      let wordFound = false

      // Filtrar palabras muy cortas (menos de 3 caracteres)
      if (searchWord.length < 3) {
        notFoundWords.push(searchWord)
        return
      }

      foodItems.forEach(item => {
        const foodName = item.dataset.foodName
        const checkbox = item.querySelector('.food-dislike-toggle')
        const foodNameElement = item.querySelector('.food-item-name')

        // Verificar si esta palabra específica coincide con el nombre del alimento
        // Solo coincidencias exactas o al inicio de palabra (no en el medio)
        const isMatch = foodName === searchWord ||
                       foodName.startsWith(searchWord + ' ') ||
                       foodName.startsWith(searchWord + '-') ||
                       foodName.endsWith(' ' + searchWord) ||
                       foodName.endsWith('-' + searchWord) ||
                       foodName.includes(' ' + searchWord + ' ') ||
                       foodName.includes('-' + searchWord + '-') ||
                       foodName.includes(' ' + searchWord + '-') ||
                       foodName.includes('-' + searchWord + ' ')

        if (isMatch) {
          wordFound = true
          foundWords.add(searchWord)

          // Solo procesar si no se ha procesado antes
          if (!foundItems.includes(item)) {
            foundItems.push(item)
            item.classList.add('search-highlight')
            foodNameElement.classList.add('search-matched')

            // Si no está marcado, agregarlo a la lista para guardar
            if (!checkbox.checked) {
              itemsToSave.push({
                item: item,
                checkbox: checkbox,
                componentId: checkbox.dataset.componentId,
                componentName: checkbox.dataset.componentName
              })
            } else {
              // Si ya está marcado, agregarlo a la lista de ya marcados
              alreadyMarkedItems.push(checkbox.dataset.componentName)
            }
          }
        }
      })

      // Si la palabra no se encontró, agregarla a la lista de no encontradas
      if (!wordFound) {
        notFoundWords.push(searchWord)
      }
    })

    // Quitar resaltado de elementos que no coinciden
    foodItems.forEach(item => {
      const foodName = item.dataset.foodName
      const foodNameElement = item.querySelector('.food-item-name')

      const hasAnyMatch = Array.from(foundWords).some(word => foodName.includes(word))
      if (!hasAnyMatch) {
        item.classList.remove('search-highlight')
        foodNameElement.classList.remove('search-matched')
      }
    })

    // Mostrar resultado de búsqueda solo si no hay ingredientes para guardar
    if (foundItems.length === 0) {
      // No se encontró nada
      this.showStatusMessage(`No se encontraron ingredientes que coincidan con "${searchTerm}"`, 'warning')
    } else if (itemsToSave.length === 0) {
      // Se encontraron ingredientes pero no hay nada para guardar (ya estaban marcados)
      let message = ''

      // Agregar información sobre ingredientes ya marcados
      if (alreadyMarkedItems.length > 0) {
        const alreadyMarkedText = alreadyMarkedItems.length === 1
          ? `1 ingrediente (${alreadyMarkedItems[0]}) ya se encuentra guardado`
          : `${alreadyMarkedItems.length} ingredientes (${alreadyMarkedItems.join(', ')}) ya se encuentran guardados`
        message = alreadyMarkedText
      }

      // Agregar información sobre palabras no encontradas
      if (notFoundWords.length > 0) {
        if (message) {
          message += `. No se encontraron coincidencias para '${notFoundWords.join(', ')}'`
        } else {
          message = `No se encontraron coincidencias para '${notFoundWords.join(', ')}'`
        }
      }

      // Determinar el tipo de mensaje
      let messageType = 'info'
      if (foundWords.size === 0 && notFoundWords.length > 0) {
        messageType = 'warning'
      }

      this.showStatusMessage(message, messageType)
    }

    // Guardar en la DB los elementos que no estaban marcados
    if (itemsToSave.length > 0) {
      this.showStatusMessage(`Guardando ${itemsToSave.length} ingrediente(s) en la base de datos...`, 'info')

      try {
        // Guardar todos los elementos encontrados
        const savedItems = []
        const failedItems = []

        for (const itemData of itemsToSave) {
          try {
            await this.createDislike(itemData.componentId, itemData.componentName)
            // Marcar visualmente como seleccionado (el toggle ya se marca automáticamente)
            itemData.checkbox.checked = true
            savedItems.push(itemData.componentName)
          } catch (itemError) {
            console.error(`Error saving ${itemData.componentName}:`, itemError)
            failedItems.push(itemData.componentName)
          }
        }

        let finalMessage = ''

        // Mensaje de ingredientes agregados exitosamente
        if (savedItems.length > 0) {
          if (savedItems.length === 1) {
            finalMessage = `1 ingrediente (${savedItems[0]}) agregado exitosamente`
          } else {
            finalMessage = `${savedItems.length} ingredientes (${savedItems.join(', ')}) agregados exitosamente`
          }
        }

        // Mensaje de ingredientes que fallaron
        if (failedItems.length > 0) {
          if (finalMessage) {
            finalMessage += `. Error al guardar: ${failedItems.join(', ')}`
          } else {
            finalMessage = `Error al guardar: ${failedItems.join(', ')}`
          }
        }

        // Agregar información sobre palabras no encontradas
        if (notFoundWords.length > 0) {
          if (finalMessage) {
            finalMessage += `. No se encontraron coincidencias para '${notFoundWords.join(', ')}'`
          } else {
            finalMessage = `No se encontraron coincidencias para '${notFoundWords.join(', ')}'`
          }
        }

        // Agregar información sobre ingredientes ya marcados
        if (alreadyMarkedItems.length > 0) {
          const alreadyMarkedText = alreadyMarkedItems.length === 1
            ? `1 ingrediente (${alreadyMarkedItems[0]}) ya se encuentra guardado`
            : `${alreadyMarkedItems.length} ingredientes (${alreadyMarkedItems.join(', ')}) ya se encuentran guardados`

          if (finalMessage) {
            finalMessage += `. ${alreadyMarkedText}`
          } else {
            finalMessage = alreadyMarkedText
          }
        }

        // Determinar el tipo de mensaje
        let messageType = 'success'
        if (failedItems.length > 0) {
          messageType = 'warning'
        }

        this.showStatusMessage(finalMessage, messageType)
      } catch (error) {
        console.error('Error saving search results:', error)
        this.showStatusMessage('Error al guardar algunos ingredientes', 'danger')
      }
    }

    // Ocultar spinner y limpiar el input después de 2 segundos
    setTimeout(() => {
      this.hideSpinner()
      const searchInput = document.getElementById('food-search-input')
      if (searchInput) {
        searchInput.value = ''
        this.clearSearchResults()
      }
    }, itemsToSave.length > 0 ? 2000 : 1500) // Menos tiempo si no hay nada que guardar
  }

  showSpinner() {
    const spinner = document.getElementById('search-spinner')
    if (spinner) {
      spinner.classList.remove('d-none')
    }
  }

  hideSpinner() {
    const spinner = document.getElementById('search-spinner')
    if (spinner) {
      spinner.classList.add('d-none')
    }
  }

  clearSearchResults() {
    const foodItems = document.querySelectorAll('.food-item-toggle')
    foodItems.forEach(item => {
      item.classList.remove('search-highlight')
      const foodNameElement = item.querySelector('.food-item-name')
      foodNameElement.classList.remove('search-matched')
    })
  }
}
